import expenseResource from "@/shared/resource/expense";
import { authorizedAxios } from "./authorizedAxios";

import {
    CalendarEntriesResult,
    ConsumerBankAccountsResponse,
    Expense,
    ExpenseAmountGroupedByCategory,
    ExpenseBalanceSummary,
    ExpenseEditRequest,
    ExpenseEditResponse,
    ExpenseEligibilityGroup,
    ExpenseEvidenceUploadResponse,
    ExpenseGroup,
    ExpenseReimbursedSummaryResponse,
    ExpenseReimburseMeLimit,
    ExpenseRequest,
    ExpenseResponse,
    ExpenseSubmitLogItem,
    ExpenseUnreimburseGetResponse,
    FlowIdUsageResponse,
    GenerateScheduleCalendarEntriesRequest,
    GetHsabExpenseCategoriesResponse,
    HsabExpenseByExternalIdRequest,
    HsabExpenseEvidenceResponse,
    HsabExpenseEvidenceShoeboxPagedResponse,
    HsabExpenseEvidenceShoeboxUploadResponse,
    HsabExpenseEvidenceUploadResponse,
    HsabExpensePagedResponse,
    HsabExpenseRequest,
    HsabExpenseResponse,
    HsabSubmitExpenseRequest,
    ImportedExpense,
    LinkedCardsResponse,
    LinkedHsabExpenseResponse,
    ManagedExpensePagedResponse,
    ManagedExpensePageRequest,
    ProviderTypeAheadResponse,
    ReimbursementExpensesByBankTransactionsGetResponse,
    ReimbursementExpensesGetResponse,
    ReimbursementRequest,
    ReimbursementRequestResponse,
    TalonClaimExpenseStatuses,
    TransferSchedule,
    UnreimbursedGraphResponse,
    UnusedDistributionResponse,
    UpdateExpensesEligibilityStatusResponse,
    UpdateReimbursementRequest,
} from "@/shared/types";

import { FinicityLinkedAccount } from "@/admin/types";

export function getLinkedCards () {
    return authorizedAxios.get<LinkedCardsResponse>(expenseResource.getLinkedCards());
}

export function getLikelyExpenses (page: number, pageSize: number) {
    return authorizedAxios.get<ExpenseGroup>(
        expenseResource.getExpenses(page, pageSize, ExpenseEligibilityGroup.LikelyEligible));
}

export function getPotentialExpenses (page: number, pageSize: number) {
    return authorizedAxios.get<ExpenseGroup>(
        expenseResource.getExpenses(page, pageSize, ExpenseEligibilityGroup.PotentialEligible));
}

export function getUnlikelyExpenses (page: number, pageSize: number) {
    return authorizedAxios.get<ExpenseGroup>(
        expenseResource.getExpenses(page, pageSize, ExpenseEligibilityGroup.UnlikelyEligible));
}

export function getExpenses (request: ExpenseRequest) {
    return authorizedAxios.get<ExpenseGroup>(
        expenseResource.getExpensesNew(), request);
}

export function getGeneratedCalendarEntriesForSchedule (request: GenerateScheduleCalendarEntriesRequest) {
    return authorizedAxios.post<CalendarEntriesResult>(expenseResource.getGeneratedCalendarEntriesForSchedule(), request);
}

export function getSubmittedHsabExpenses (page: number, pageSize: number) {
    return authorizedAxios.get<HsabExpensePagedResponse>(
        expenseResource.getSubmittedHsabExpenses(page, pageSize));
}

export function getAvailableHsabExpenses (page: number, pageSize: number) {
    return authorizedAxios.get<HsabExpensePagedResponse>(
        expenseResource.getAvailableHsabExpenses(page, pageSize));
}

export function getManagedExpensesPage (request: ManagedExpensePageRequest, args?: { username: string, partnerId: number }) {
    const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
    return authorizedAxios.get<ManagedExpensePagedResponse>(expenseResource.getManagedExpensesPage(request), params);
}

export function exportManagedExpenses (request: ManagedExpensePageRequest) {
    return authorizedAxios.get<string>(expenseResource.exportManagedExpenses(request));
}
export function exportManagedExpensesUrl (request: ManagedExpensePageRequest) {
    return authorizedAxios.get<string>(expenseResource.exportManagedExpensesPresignedUrl(request));
}
export function getHsabExpenseEvidenceShoeboxByPage (page: number, pageSize: number) {
    return authorizedAxios.get<HsabExpenseEvidenceShoeboxPagedResponse>(expenseResource.getHsabExpenseEvidenceShoeboxByPage(page, pageSize));
}

export function getHsabExpenseStatusesByIds (expenseIds: number[]) {
    const request = { expenseIds };
    return authorizedAxios.get<TalonClaimExpenseStatuses[]>(expenseResource.getHsabExpenseStatusesByIds(), request);
}

export function getUserHasHealthPlanExpense () {
    return authorizedAxios.get<boolean>(expenseResource.getUserHasHealthPlanExpense());
}

export function createOrUpdateHsabExpense (request: HsabExpenseRequest) {
    return authorizedAxios.post<HsabExpenseResponse>(expenseResource.createOrUpdateHsabExpense(), request);
}

export function submitHsabExpense (request: HsabSubmitExpenseRequest) {
    return authorizedAxios.post<HsabExpenseResponse>(expenseResource.submitHsabExpense(request.expenseId), request);
}

export function ActivateHsabExpenseSchedule (request: HsabSubmitExpenseRequest) {
    return authorizedAxios.post<HsabExpenseResponse>(expenseResource.ActivateHsabExpenseSchedule(), request);
}

export function deactivateHsabExpenseSchedule (hsabExpenseScheduleId: number) {
    return authorizedAxios.post<TransferSchedule>(expenseResource.DeactivateHsabExpenseSchedule(hsabExpenseScheduleId));
}

export function GetUserHasAnyRecurringExpenses () {
    return authorizedAxios.get<boolean>(expenseResource.GetUserHasAnyRecurringExpenses());
}

export function submitHsabExpenseEvidence (expenseId: number) {
    return authorizedAxios.post(expenseResource.submitHsabExpenseEvidence(expenseId));
}

export function uploadFilesToHsabExpense (expenseId: number, files: File[]) {
    return authorizedAxios.postFiles<HsabExpenseEvidenceUploadResponse>(
        expenseResource.uploadFilesToHsabExpense(expenseId), files);
}

export function CreateEvidenceAndMoveShoeboxFiles (expenseId: number, hsabExpenseEvidenceShoeboxIds: number[]) {
    return authorizedAxios.post(expenseResource.CreateEvidenceAndMoveShoeboxFiles(expenseId), hsabExpenseEvidenceShoeboxIds);
}

export function syncHsabExpenses () {
    return authorizedAxios.post<boolean>(expenseResource.syncHsabExpenses());
}

export function getHsabExpenseCategories () {
    return authorizedAxios.get<GetHsabExpenseCategoriesResponse>(expenseResource.getHsabExpenseCategories());
}
export function getExpenseReimburseMeLimit () {
    return authorizedAxios.get<ExpenseReimburseMeLimit>(expenseResource.getExpenseReimburseMeLimit());
}
export function deleteHsabExpense (expenseId: number) {
    return authorizedAxios.delete(expenseResource.DeleteHsabExpense(expenseId));
}

export function repayHsabExpenseReimbursement (hsabExpenseReimbursementId: number) {
    return authorizedAxios.post(expenseResource.RepayHsabExpenseReimbursement(hsabExpenseReimbursementId));
}

export function getHsabExpenseById (expenseId: number, args?: { username: string, partnerId: number }) {
    const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
    return authorizedAxios.get<HsabExpenseResponse>(expenseResource.getHsabExpenseById(expenseId), params);
}

export function getHsabExpenseWithRelationshipsById (expenseId: number, args?: { username: string, partnerId: number }) {
    const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
    return authorizedAxios.get<HsabExpenseResponse>(expenseResource.getHsabExpenseWithRelationshipsById(expenseId), params);
}
export function getSubmitLogsById (expenseId: number, args?: { username: string, partnerId: number }) {
    const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
    return authorizedAxios.get<ExpenseSubmitLogItem[]>(expenseResource.getSubmitLogsById(expenseId), params);
}
export function GetSubmitLogByFileName (expenseId: number, filename: string, args?: { username: string, partnerId: number }) {
    const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
    return authorizedAxios.get<string>(expenseResource.GetSubmitLogByFileName(expenseId, filename), params);
}
export function getHsabExpenseByExternalId (request: HsabExpenseByExternalIdRequest) {
    return authorizedAxios.get<HsabExpenseResponse>(expenseResource.getHsabExpenseByExternalId(request));
}

export function doesFlowIdHaveAssociation (flowIds: string[]) {
    return authorizedAxios.get<FlowIdUsageResponse[]>(expenseResource.doesFlowIdHaveAssociation(flowIds));
}


export function searchExpenses (pageNumber: number,
                                pageSize: number,
                                startDate: string,
                                endDate: string,
                                description: string,
                                excludedExpenseIds: number[]) {

    const excludedExpenseIdsParam = excludedExpenseIds.join(`,`);

    const query = {
        page: pageNumber,
        pageSize,
        startDate,
        endDate,
        description,
        excludedExpenseIds: excludedExpenseIdsParam
    };

    return authorizedAxios.get<ExpenseResponse>(expenseResource.searchExpenses(), query);
}

export function getHsabExpensesByIds (expenseIds = []) {
    const request = { expenseIds };
    return authorizedAxios.get<LinkedHsabExpenseResponse[]>(expenseResource.getHsabExpensesByIds(), request);
}
export function getExpensesByIds (ids: number[]) {
    return authorizedAxios.post<Expense[]>(
        expenseResource.getExpensesByIds(), ids);
}

export function setEligibilityStatus (expenseStatusUpdates) {
    return authorizedAxios.put<UpdateExpensesEligibilityStatusResponse>(expenseResource.putEligibilityStatus(),
        expenseStatusUpdates);
}

export function getBalanceSummary () {
    return authorizedAxios.get<ExpenseBalanceSummary>(expenseResource.getBalanceSummary());
}

export function editExpense (request: ExpenseEditRequest, expenseId?: number) {
    return authorizedAxios.post<ExpenseEditResponse>(expenseResource.expenseEdit(expenseId), request);
}
export function getProviderSearchTypeAhead (searchTerm: string) {
    return authorizedAxios.get<ProviderTypeAheadResponse>(expenseResource.getProviderSearchTypeAhead(searchTerm));
}
export function getProviderLookupPreCache () {
    return authorizedAxios.get(expenseResource.getProviderLookupPreCache());
}
export function addExpenseEvidence (files, expenseId: number) {
    return authorizedAxios.postFiles<ExpenseEvidenceUploadResponse>(expenseResource.expenseEvidence(expenseId), files);
}

export function getReimbursementMonthlySummary (startDate: string, endDate: string) {
    return authorizedAxios.get<ExpenseReimbursedSummaryResponse>(
        expenseResource.reimbursedMonthlySummary(startDate, endDate));
}

export function removeExpenseEvidence (expenseId: number, filename: string) {
    return authorizedAxios.delete(expenseResource.expenseEvidence(expenseId, filename));
}

export function expenseEvidenceGet (expenseId: number, filename: string) {
    return authorizedAxios.getFile(expenseResource.expenseEvidence(expenseId, filename));
}

export function expenseEvidenceGetDownloadLink (expenseId: number, filename: string) {
    return authorizedAxios.get<string>(expenseResource.expenseEvidenceS3PreSignedUrl(expenseId, filename));
}

export function reimbursement (request: ReimbursementRequest) {
    return authorizedAxios.post(expenseResource.reimbursement(), request);
}

export function unreimbursedGet (page: number, pageSize: number, receipts) {
    return authorizedAxios.get<ExpenseUnreimburseGetResponse>(expenseResource.unreimbursed(page, pageSize, receipts));
}

export function unreimbursedGetCount () {
    return authorizedAxios.get<number>(expenseResource.unreimbursedCount());
}

export function unreimbursedWithoutAttachmentsCount () {
    return authorizedAxios.get<number>(expenseResource.unreimbursedWithoutAttachmentsCount());
}

export function reimbursedGet (page: number, pageSize: number, excludeFailures = false) {
    return authorizedAxios.get<ExpenseUnreimburseGetResponse>(
        expenseResource.reimbursed(page, pageSize, excludeFailures));
}

export function reimbursedExpensesByReimbursementId (reimbursementId: number) {
    return authorizedAxios
        .get<ReimbursementExpensesGetResponse>(expenseResource.reimbursedExpensesByReimbursementId(reimbursementId));
}

export function reimbursedExpensesByBankTransactionIds (ids: string[]) {
    return authorizedAxios.get<ReimbursementExpensesByBankTransactionsGetResponse>(
        expenseResource.reimbursedExpensesByBankTransactionIds(ids.join(`%2C`))
    );
}

export function unreimbursedGraphGet (startDate: string, endDate: string) {
    return authorizedAxios.get<UnreimbursedGraphResponse>(expenseResource.unreimbursedGraph(startDate, endDate));
}

export function reimbursementRequest (request: ReimbursementRequest) {
    return authorizedAxios.post<ReimbursementRequestResponse>(expenseResource.createReimbursementRequest(), request);
}

export function updateReimbursementRequest (request: UpdateReimbursementRequest) {
    return authorizedAxios.put(expenseResource.updateReimbursementRequest(request.reimbursementRequestId), request);
}

export function getExpenseAmountByCategory (year?: number) {
    return authorizedAxios.get<ExpenseAmountGroupedByCategory>(expenseResource.expenseAmountGroupByCategory(year));
}

export function importedExpenseEvidenceGetDownloadLink (expenseId: number, fileName: string) {
    return authorizedAxios.get<string>(expenseResource.importedExpenseEvidenceDownload(expenseId, fileName));
}

export function getImportedExpenses () {
    return authorizedAxios.get<ImportedExpense>(expenseResource.getImportedExpenses());
}

export function manualMarkedAsReimbursed (request: ReimbursementRequest) {
    return authorizedAxios.post<any>(expenseResource.manualMarkedAsReimbursed(), request);
}

export function deleteExpense (expenseId: number) {
    return authorizedAxios.delete(expenseResource.deleteExpense(expenseId));
}

export function getHsabExpenseEvidence (expenseId: number) {
    return authorizedAxios.get<HsabExpenseEvidenceResponse[]>(expenseResource.getExpenseEvidence(expenseId));
}

export function deleteHsabExpenseEvidence (expenseId: number, expenseEvidenceId: number) {
    return authorizedAxios.delete(expenseResource.deleteExpenseEvidence(expenseId, expenseEvidenceId));
}

export function retrySubmitHsabExpenseEvidence (expenseId: number, expenseEvidenceId: number) {
    return authorizedAxios.post<HsabExpenseEvidenceResponse>(
        expenseResource.retrySubmitHsabExpenseEvidence(expenseId, expenseEvidenceId));
}

export function getHsabExpenseEvidenceSignedS3Url (expenseId: number, expenseEvidenceId: number, args?: { username: string, partnerId: number }) {
    const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
    return authorizedAxios.get<string>(
        expenseResource.getHsabExpenseEvidenceSignedS3Url(expenseId, expenseEvidenceId), params);
}

export function getHsabExpenseEvidenceShoeboxSignedS3Url (shoeboxId: number) {
    return authorizedAxios.get<string>(
        expenseResource.getHsabExpenseEvidenceShoeboxSignedS3Url(shoeboxId));
}

export function getAdminFinicityLinkedAccounts (cognitoUsername: string) {
    return authorizedAxios.get<FinicityLinkedAccount[]>(
        expenseResource.getAdminFinicityLinkedAccounts(cognitoUsername));
}
export function getConsumerBankAccounts () {
    return authorizedAxios.get<ConsumerBankAccountsResponse[]>(expenseResource.getConsumerBankAccounts());
}

export function getUnusedDistributionById (reimbursementId: number, args?: { username: string, partnerId: number }) {
    const params = args ? { forUser: args.username, forUserPartnerId: args.partnerId } : undefined;
    return authorizedAxios.get<UnusedDistributionResponse>(expenseResource.getUnusedDistributionById(reimbursementId), params);
}
export function deleteShoeboxFile (shoeboxFileId: number) {
    return authorizedAxios.delete<boolean>(
        expenseResource.deleteShoeboxFile(shoeboxFileId));
}
export function createAndUploadShoeboxFiles (files: File[]) {
    return authorizedAxios.postFiles<HsabExpenseEvidenceShoeboxUploadResponse>(
        expenseResource.createAndUploadShoeboxFiles(), files);
}